import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useState,
} from 'react';

import { LocalStorageKey } from '@/shared/config';
import { safeLocalStorage } from '@/shared/lib';

import { NAME_COLUMN_MAX_WIDTH, NAME_COLUMN_MIN_WIDTH } from '../config';

const clampWidth = (width: number): number =>
  Math.min(
    NAME_COLUMN_MAX_WIDTH,
    Math.max(NAME_COLUMN_MIN_WIDTH, Math.round(width)),
  );

const readStoredWidth = (): number | null => {
  const raw = safeLocalStorage.getItem(
    LocalStorageKey.templateListNameColumnWidth,
  );
  const parsed = raw === null ? Number.NaN : Number(raw);

  return Number.isFinite(parsed) && parsed > 0 ? clampWidth(parsed) : null;
};

export const useTemplateNameColumnWidth = () => {
  const [width, setWidth] = useState<number | null>(readStoredWidth);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) {
        return;
      }

      const handle = event.currentTarget;
      const header = handle.closest('th');
      const startX = event.clientX;
      const startWidth = width ?? header?.offsetWidth ?? NAME_COLUMN_MIN_WIDTH;
      const widthAt = (clientX: number) =>
        clampWidth(startWidth + clientX - startX);

      // jsdom has no pointer capture; the browser needs it so a fast drag
      // keeps reporting to the handle after the cursor leaves it.
      handle.setPointerCapture?.(event.pointerId);
      event.preventDefault();

      const handleMove = (moveEvent: PointerEvent) => {
        setWidth(widthAt(moveEvent.clientX));
      };

      const stopListening = (pointerId: number) => {
        handle.removeEventListener('pointermove', handleMove);
        handle.removeEventListener('pointerup', finish);
        handle.removeEventListener('pointercancel', cancel);
        handle.releasePointerCapture?.(pointerId);
      };

      const finish = (upEvent: PointerEvent) => {
        stopListening(upEvent.pointerId);

        const finalWidth = widthAt(upEvent.clientX);
        setWidth(finalWidth);
        safeLocalStorage.setItem(
          LocalStorageKey.templateListNameColumnWidth,
          String(finalWidth),
        );
      };

      const cancel = (cancelEvent: PointerEvent) => {
        stopListening(cancelEvent.pointerId);
        setWidth(width);
      };

      handle.addEventListener('pointermove', handleMove);
      handle.addEventListener('pointerup', finish);
      handle.addEventListener('pointercancel', cancel);
    },
    [width],
  );

  return { width, startResize };
};
